import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import {
  Send,
  AlertTriangle,
  Clock,
  Flag,
  UserMinus,
  Mail,
  Server,
  ShieldCheck,
  ShieldAlert,
  Eye,
  Ban,
  Unplug,
} from "lucide-react";
import { KPICard, pct } from "./KPICard";
import { SectionHeader } from "./HelperText";
import { useEmailReputationData } from "../../hooks/useEmailReputationData";

// Checkpoint 3.5D. Two genuinely different data-confidence categories on
// this page, and they must never look the same:
//
// 1. "derivable_not_exposed" (Reputation Health Summary, Environment
//    Comparison) — the underlying SendGrid event vocabulary is real and
//    verified (delivered/bounce/deferred/spamreport/unsubscribe are
//    recognized SendGridProviderEventType values), just not yet
//    aggregated by any backend endpoint. Rendered with KPICard's existing
//    Metric Confidence Standard (status="estimated"), the SAME pattern
//    Overview/Performance already use for the same reason.
//
// 2. "pending_integration" (Mailbox Provider Reputation, Sending IP
//    Health, Authentication, Blacklist/Inbox Placement) — MissionAssist
//    has NO integration of any kind with these sources today (no outbound
//    SendGrid API client, no Google/Yahoo/Microsoft postmaster
//    integration, no IP inventory, no domain-authentication verification
//    — see emailReputationMockAdapter.js's header for the full repository
//    evidence). These are rendered with the neutral IntegrationStatusBadge
//    below — deliberately never colored green, never a checkmark, never
//    styled identically to a real "Healthy" signal. Absence of data must
//    never look like a healthy result.
function IntegrationStatusBadge({ label = "Pending Integration" }) {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-400 bg-slate-500/10 ring-1 ring-slate-500/20"
      data-testid="reputation-pending-integration-badge"
    >
      <Unplug className="h-3 w-3" aria-hidden="true" />
      {label}
    </span>
  );
}

const SUMMARY_KPI_DEFS = [
  { key: "deliveryRate", label: "Delivery Rate", icon: Send },
  { key: "bounceRate", label: "Bounce Rate", icon: AlertTriangle },
  { key: "deferredRate", label: "Deferred Rate", icon: Clock },
  { key: "complaintRate", label: "Spam Complaint Rate", icon: Flag },
  { key: "unsubscribeRate", label: "Unsubscribe Rate", icon: UserMinus },
];

const METRIC_REASON = "Derived from real SendGrid event vocabulary; no backend aggregation endpoint exists yet.";

function ReputationSkeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="reputation-skeleton">
      {SUMMARY_KPI_DEFS.map((def) => (
        <Card key={def.key} className="glass border-white/10 animate-pulse">
          <CardContent className="p-4 space-y-3">
            <div className="h-3 w-20 rounded bg-white/10" />
            <div className="h-6 w-16 rounded bg-white/10" />
            <div className="h-3 w-24 rounded bg-white/5" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function MailboxProviderCard({ provider }) {
  return (
    <Card className="glass border-white/10" data-testid={`reputation-provider-${provider.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-sm font-medium text-slate-100">
            <Mail className="h-4 w-4 text-slate-400" />
            {provider.label}
          </span>
          <IntegrationStatusBadge />
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <p className="text-slate-500">Delivery Rate (preview)</p>
            <p className="text-slate-400 font-mono">{pct(provider.previewDeliveryRate)}</p>
          </div>
          <div>
            <p className="text-slate-500">Bounce/Failure Rate (preview)</p>
            <p className="text-slate-400 font-mono">{pct(provider.previewBounceRate)}</p>
          </div>
        </div>
        <div className="text-xs">
          <span className="text-slate-500">Complaint/Spam indicator: </span>
          <span className="text-slate-400">{provider.previewComplaintIndicator} (preview)</span>
        </div>
        <p className="text-[11px] text-slate-500">Future source: {provider.futureSource}</p>
      </CardContent>
    </Card>
  );
}

/**
 * EmailReputationView — Checkpoint 3.5D.
 *
 * The Email tab's "Reputation" view. Distinct product direction from
 * 3.5A/3.5B/3.5C: rather than deferring because most reputation signals
 * (mailbox-provider reputation, sending-IP health, domain authentication,
 * blacklist/inbox placement) have zero current backend or third-party
 * integration, this page establishes the intended operator experience now
 * on deterministic mocks/placeholders, with every unintegrated section
 * explicitly and consistently marked "Pending Integration" — never
 * presented as if it reflects a real, healthy signal.
 *
 * Consumes only the APPLIED Environment value (never draft), mirroring
 * every other Email view's contract. Period/Broadcast/Program/Campaign
 * are not accepted — none has a defined meaning here (see
 * EmailFilterBar.jsx's EMAIL_FILTER_BAR_FIELDS.environmentOnly, reused
 * unchanged for this view).
 */
export default function EmailReputationView({ environment }) {
  const {
    loading,
    error,
    summary,
    mailboxProviders,
    sendingIps,
    authentication,
    blacklistInboxPlacement,
    environmentComparison,
    rateAvailability,
  } = useEmailReputationData({ environment });

  const hasData = !!summary;

  return (
    <div className="space-y-6" data-testid="email-reputation-view">
      {error && (
        <Card className="glass border-red-500/20" data-testid="reputation-error">
          <CardContent className="p-4 text-sm text-red-300">Failed to load Reputation data: {error}</CardContent>
        </Card>
      )}

      {!error && loading && <ReputationSkeleton />}

      {!error && !loading && hasData && (
        <>
          {/* A. Reputation Health Summary */}
          <div>
            <SectionHeader
              title="Reputation Health Summary"
              helper="Aggregate operational indicators — real SendGrid event vocabulary, not yet backed by a live aggregation endpoint."
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="reputation-summary-grid">
              {SUMMARY_KPI_DEFS.map((def) => {
                // EMAIL-DELIVERY-TRACKING-004A metric correction: a rate
                // the real EDT dashboard endpoint has explicitly marked
                // unavailable (deliveryRate/bounceRate today -- no
                // authoritative attempted-send denominator exists) must
                // render as honestly unavailable, never as a fabricated
                // mock percentage -- even though every other KPI on this
                // same grid stays "estimated". See KPICard's own
                // Availability axis (DASH-EMAIL-010) for why this is a
                // real, existing mechanism, not a new one invented here.
                const unavailable = rateAvailability[def.key];
                return (
                  <KPICard
                    key={def.key}
                    title={def.label}
                    value={unavailable ? undefined : summary[def.key]}
                    format="percent"
                    icon={def.icon}
                    status={unavailable ? undefined : "estimated"}
                    reason={unavailable ? undefined : METRIC_REASON}
                    availability={unavailable ? "UNAVAILABLE" : undefined}
                    availabilityReason={unavailable ? unavailable.reason : undefined}
                  />
                );
              })}
            </div>
          </div>

          {/* B. Mailbox Provider Reputation */}
          <div>
            <SectionHeader
              title="Mailbox Provider Reputation"
              helper="No provider integration exists today — every card below is preview data pending Google Postmaster / Yahoo / Microsoft integration."
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="reputation-provider-grid">
              {mailboxProviders.map((provider) => (
                <MailboxProviderCard key={provider.id} provider={provider} />
              ))}
            </div>
          </div>

          {/* C. Sending IP Health */}
          <Card className="glass border-white/10" data-testid="reputation-ip-health-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
                <Server className="h-5 w-5 text-indigo-400" />
                Sending IP Health
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-slate-400">Sending IP</TableHead>
                    <TableHead className="text-slate-400">Environment</TableHead>
                    <TableHead className="text-slate-400">Type</TableHead>
                    <TableHead className="text-right text-slate-400">Delivery Rate</TableHead>
                    <TableHead className="text-right text-slate-400">Bounce Rate</TableHead>
                    <TableHead className="text-right text-slate-400">Deferred Rate</TableHead>
                    <TableHead className="text-slate-400">Reputation</TableHead>
                    <TableHead className="text-slate-400">Warm-up</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sendingIps.map((row) => (
                    <TableRow key={row.ip} className="border-white/10 hover:bg-white/5" data-testid={`reputation-ip-row-${row.ip}`}>
                      <TableCell className="font-mono text-slate-100">{row.ip}</TableCell>
                      <TableCell className="capitalize text-slate-300" data-testid={`reputation-ip-environment-${row.ip}`}>
                        {row.environment}
                      </TableCell>
                      <TableCell className="text-slate-400" data-testid={`reputation-ip-type-${row.ip}`}>
                        {row.ipTypeLabel}
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-400">{pct(row.previewDeliveryRate)}</TableCell>
                      <TableCell className="text-right font-mono text-slate-400">{pct(row.previewBounceRate)}</TableCell>
                      <TableCell className="text-right font-mono text-slate-400">{pct(row.previewDeferredRate)}</TableCell>
                      <TableCell data-testid={`reputation-ip-reputation-status-${row.ip}`}>
                        <IntegrationStatusBadge />
                      </TableCell>
                      <TableCell data-testid={`reputation-ip-warmup-status-${row.ip}`}>
                        <IntegrationStatusBadge label="Warm-up Pending" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-[11px] text-slate-500 mt-2">
                Future source: SendGrid account / IP inventory, event aggregation by sending_ip, warm-up state.
              </p>
            </CardContent>
          </Card>

          {/* D. Domain / Authentication Health — deliberately NOT inferred
              from individual bounce-reason text (the only related signal
              that currently exists); every entry is explicitly unverified. */}
          <Card className="glass border-white/10" data-testid="reputation-authentication-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
                <ShieldCheck className="h-5 w-5 text-indigo-400" />
                Domain / Authentication Health
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {Object.values(authentication).map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col items-start gap-1.5 rounded-lg border border-white/10 bg-white/[0.02] p-3"
                  data-testid={`reputation-auth-${item.id}`}
                >
                  <span className="text-sm text-slate-200">{item.label}</span>
                  <IntegrationStatusBadge label="Not Yet Verified" />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* E. Blacklist / Inbox Placement — unconditionally "not yet
              connected"; there is no code path that could render this as
              Healthy, since no data source exists to make that claim. */}
          <Card className="glass border-white/10" data-testid="reputation-blacklist-inbox-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
                <ShieldAlert className="h-5 w-5 text-indigo-400" />
                Blacklist / Inbox Placement
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid="reputation-blocklist-status">
                <span className="flex items-center gap-1.5 text-sm text-slate-200">
                  <Ban className="h-4 w-4 text-slate-400" />
                  Blocklist Status
                </span>
                <span className="text-xs text-slate-400">Not yet connected</span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid="reputation-inbox-placement-status">
                <span className="flex items-center gap-1.5 text-sm text-slate-200">
                  <Eye className="h-4 w-4 text-slate-400" />
                  Inbox Placement
                </span>
                <span className="text-xs text-slate-400">Not yet connected</span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid="reputation-spam-folder-status">
                <span className="flex items-center gap-1.5 text-sm text-slate-200">
                  <Eye className="h-4 w-4 text-slate-400" />
                  Spam Folder Placement
                </span>
                <span className="text-xs text-slate-400">Not yet connected</span>
              </div>
              <p className="text-[11px] text-slate-500">
                Last checked: {blacklistInboxPlacement.lastChecked || "—"} · Future source: blocklist monitoring / seed-list
                inbox-placement provider.
              </p>
            </CardContent>
          </Card>

          {/* F. Environment Comparison */}
          <Card className="glass border-white/10" data-testid="reputation-environment-comparison-card">
            <CardHeader>
              <CardTitle className="text-lg text-slate-100">Environment Comparison</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {environmentComparison.map((row) => (
                <div
                  key={row.environment}
                  className="rounded-lg border border-white/10 bg-white/[0.02] p-3"
                  data-testid={`reputation-environment-comparison-${row.environment}`}
                >
                  <p className="text-sm font-medium capitalize text-slate-100 mb-2">{row.environment}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {SUMMARY_KPI_DEFS.map((def) => {
                      // EMAIL-DELIVERY-TRACKING-004A: same availability
                      // check as the summary grid above -- without this,
                      // an unavailable rate would render as a fabricated
                      // "0%" here (pct(undefined) => "0%"), directly
                      // contradicting the "Unavailable" state shown for
                      // the identical metric in the summary grid on the
                      // same page.
                      const unavailable = rateAvailability[def.key];
                      return (
                        <div key={def.key}>
                          <p className="text-slate-500">{def.label}</p>
                          <p className="font-mono text-slate-300">
                            {unavailable ? "—" : pct(row.summary[def.key])}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
